
import * as React from 'react';
import { IAddon, createAddon } from '../../addons/registerOperationHandler';
import { registerControlHandler, createControlHandler } from '../../addons/registerControlHandler';
import { IItemCacheDispatch, IItemCacheThunkAction } from '../../store/actions/ItemCacheActions';
import { progressControl } from '../../controls/progressControl';
import { formatToArray, getLocalizedCountValue } from '@ms/utilities-strings/lib/index';
import { IItemCache } from '../../models/IItemCacheObserver';
import { getOverallSimpleProgressBreakdown } from '../progress';
import DeleteStrings from './DeleteStrings.resx';
import { ProgressPhase, sourceItemFacet } from '../../facets/ProgressFacets';
import { ItemLink } from '../../components/itemLink/ItemLink';
import { deleteProgressKeyFacet } from '../deleteItems';
import { childItemsFacet } from '../../facets/SetFacets';
import { parentFolderItemFacet } from '../../facets/ItemFacets';

interface ISummaryFormatterProps {
  count: number;
}

interface IGroupFormatterProps {
  count: number;
  itemKey: string;
}

const summaryFormatters = {
  [ProgressPhase.started]: ({ count }: ISummaryFormatterProps) => formatToArray(getLocalizedCountValue(
    DeleteStrings.deleteStartedItemsTitle,
    DeleteStrings.deleteCompletedItemsTitleIntervals,
    count) || '',
    count),
  [ProgressPhase.completed]: ({ count }: ISummaryFormatterProps) => formatToArray(getLocalizedCountValue(
    DeleteStrings.deleteCompletedItemsTitle,
    DeleteStrings.deleteCompletedItemsTitleIntervals,
    count) || '',
    count),
  [ProgressPhase.failed]: ({ count }: ISummaryFormatterProps) => formatToArray(getLocalizedCountValue(
    DeleteStrings.deleteFailedItemsTitle,
    DeleteStrings.deleteFailedItemsTitleIntervals,
    count) || '',
    count),
  [ProgressPhase.canceled]: ({ count }: ISummaryFormatterProps) => formatToArray(getLocalizedCountValue(
    DeleteStrings.deleteFailedItemsTitle,
    DeleteStrings.deleteFailedItemsTitleIntervals,
    count) || '',
    count),
};

const groupFormatters = {
  [ProgressPhase.started]: ({ count, itemKey }: IGroupFormatterProps) => formatToArray(getLocalizedCountValue(
    DeleteStrings.deleteStartedItemsDestinationTitle,
    DeleteStrings.deleteStartedItemsDestinationTitleIntervals,
    count) || '',
    count,
    <ItemLink key='link' itemKey={ itemKey } />),
  [ProgressPhase.completed]: ({ count, itemKey }: IGroupFormatterProps) => formatToArray(getLocalizedCountValue(
    DeleteStrings.deleteCompletedItemsDestinationTitle,
    DeleteStrings.deleteCompletedItemsDestinationTitleIntervals,
    count) || '',
    count,
    <ItemLink key='link' itemKey={ itemKey } />),
  [ProgressPhase.failed]: ({ count, itemKey }: IGroupFormatterProps) => formatToArray(getLocalizedCountValue(
    DeleteStrings.deleteFailedItemsDestinationTitle,
    DeleteStrings.deleteFailedItemsDestinationTitleIntervals,
    count) || '',
    count,
    <ItemLink key='link' itemKey={ itemKey } />),
  [ProgressPhase.canceled]: ({ count, itemKey }: IGroupFormatterProps) => formatToArray(getLocalizedCountValue(
    DeleteStrings.deleteFailedItemsDestinationTitle,
    DeleteStrings.deleteFailedItemsDestinationTitleIntervals,
    count) || '',
    count,
    <ItemLink key='link' itemKey={ itemKey } />),
};

/**
 * Addon to be registered by the DeleteAction component which manages the Progress items for deletions.
 */
export const deleteProgressAddon: IAddon = createAddon(() => {
  return (dispatch: IItemCacheDispatch) => {
    dispatch(registerControlHandler(deleteProgressKeyFacet, progressControl)(
      createControlHandler()(getDeleteItemsProgressControl)
    ));
  };
});

/**
 * Gets the progress control for deletion, which emits the strings representing the state of a deletion.
 */
function getDeleteItemsProgressControl(): IItemCacheThunkAction<typeof progressControl.type> {
  return (dispatch: IItemCacheDispatch) => {
    return getDeleteProgressOverrides;
  };
}

function getDeleteProgressOverrides(itemCache: IItemCache, props: typeof progressControl.inputType): typeof progressControl.returnType {
  const {
    phase,
    count
  } = getOverallSimpleProgressBreakdown(itemCache, props);

  const mutualParentItemKey = getMutualProgressSourceParentItemKey(itemCache, props);

  let title: React.ReactNode;

  if (props.format === 'group' && mutualParentItemKey) {
    title = groupFormatters[phase]({ count, itemKey: mutualParentItemKey });
  } else {
    title = summaryFormatters[phase]({ count });
  }

  return {
    ...(title ? {
      title
    } : {})
  };
}

/**
 * Determines the mutual parent folder item for all child progress items of the given batch.
 */
function getMutualProgressSourceParentItemKey(itemCache: IItemCache, props: { itemKey: string; }): string | undefined {
  let itemKey: string | undefined = undefined;

  const childItems = itemCache.demandItemFacet(childItemsFacet, props.itemKey);

  if (childItems) {
    for (const childItemKey of childItems.itemKeys) {
      const sourceItem = itemCache.demandItemFacet(sourceItemFacet, childItemKey);
      const sourceItemKey = sourceItem && sourceItem.itemKey;

      const parentFolderItem = itemCache.demandItemFacet(parentFolderItemFacet, sourceItemKey);
      const parentFolderItemKey = parentFolderItem && parentFolderItem.itemKey;

      if (parentFolderItemKey) {
        if (!itemKey) {
          itemKey = parentFolderItemKey;
        } else if (parentFolderItemKey !== itemKey) {
          break;
        }
      }
    }
  }

  return itemKey;
}
